import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProvince } from '../contexts/ProvinceContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useKingdom } from '../hooks/useKingdom';
import { getRandomKingdom } from '../api/kingdom';
import { Spinner } from '../components/Spinner';
import { DesktopKingdomViewerShell } from '../components/kingdomViewer/DesktopKingdomViewerShell';
import { MobileKingdomViewerShell } from '../components/kingdomViewer/MobileKingdomViewerShell';
import type { KingdomViewerShellProps } from '../components/kingdomViewer/types';

/**
 * Kingdom World Viewer — a live, public, browsable-by-anyone view of any kingdom's basic roster.
 * Explicitly *not* an intel/scouting feature (see Dossier for that).
 *
 * Route: /world/kingdoms/:kingdomId?
 */
export function KingdomViewer() {
  const { kingdomId: kingdomIdParam } = useParams<{ kingdomId?: string }>();
  const navigate = useNavigate();
  const { province } = useProvince();
  const { worldId } = useAuth();
  const platform = useAppShellPlatform();

  const ownKingdomId = province?.metadata?.location
    ? `${province.metadata.location.island}:${province.metadata.location.kingdom}`
    : null;
  const viewerProvinceId = province?.metadata?.id ?? null;

  const [kingdomId, setKingdomId] = useState<string | null>(kingdomIdParam ?? ownKingdomId);
  const [announcement, setAnnouncement] = useState('');

  const { kingdom, isLoading, error, refresh } = useKingdom(kingdomId);

  // Sync kingdomId to URL param (or fall back to own kingdom once province loads).
  useEffect(() => {
    const effective = kingdomIdParam ?? ownKingdomId;
    if (!effective) return;
    setKingdomId(effective);
  }, [kingdomIdParam, ownKingdomId]);

  // Live region announcement for screen readers navigating via Prev/Next/Random/picker — skip the
  // initial mount so a fresh page load isn't announced as if the user had just navigated.
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (kingdomId) setAnnouncement(`Now viewing Kingdom ${kingdomId}`);
  }, [kingdomId]);

  const handleSelect = (island: number, kingdomNum: number) => {
    navigate(`/world/kingdoms/${island}:${kingdomNum}`);
  };

  const handlePrevious = () => {
    if (!kingdom?.previousKingdomId) return;
    navigate(`/world/kingdoms/${kingdom.previousKingdomId}`);
  };

  const handleNext = () => {
    if (!kingdom?.nextKingdomId) return;
    navigate(`/world/kingdoms/${kingdom.nextKingdomId}`);
  };

  const handleRandom = async () => {
    if (!worldId) return;
    try {
      const result = await getRandomKingdom(worldId);
      navigate(`/world/kingdoms/${result.kingdomId}`);
    } catch (err) {
      console.error('Failed to get random kingdom:', err);
    }
  };

  // SF-169 Decision 4: disable Prev/Next during load/error rather than a key={kingdomId} remount
  // (which would flash a full-page spinner on every click).
  const previousDisabled = isLoading || !!error || !kingdom?.previousKingdomId;
  const nextDisabled = isLoading || !!error || !kingdom?.nextKingdomId;

  if (!kingdomId) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  const shellProps: KingdomViewerShellProps = {
    kingdom,
    isLoading,
    error,
    viewerProvinceId,
    pageTitle: 'World Viewer',
    emptyMessage: 'Unable to load this kingdom.',
    refresh,
    browse: {
      onSelect: handleSelect,
      onPrevious: handlePrevious,
      onNext: handleNext,
      onRandom: handleRandom,
      previousDisabled,
      nextDisabled,
    },
  };

  return (
    <div>
      <div aria-live="polite" className="sr-only">{announcement}</div>
      {platform === 'mobile'
        ? <MobileKingdomViewerShell {...shellProps} />
        : <DesktopKingdomViewerShell {...shellProps} />}
    </div>
  );
}
